import React, { useMemo } from "react";
import { CloseIcon } from "@/utils/icons";
import {
  S_ListingModalContainer,
  S_ListingBodyContainer,
  S_CloseButton,
  S_ModalTitle,
  S_ProductListContainer,
  S_ProductContainer,
  S_ProductPriceWrapper,
  S_ProductFinalPrice,
  S_GrouperPriceWrapper,
  S_ActionsContainer,
  S_GrouperBuy,
} from "./style";
import { useI18n } from "@/utils/rioti18n";
import { ProductInfoData, useProductInfo } from "@/providers/useProductInfo";
import { ProductGame, ProductType, PurchasabilityStatus } from "@/@types/common";
import { ProductInBundle } from "@/@types/data/collections";
import { getIconForProduct } from "@/assets/svgs/CurrencyIcons";

export type BundlePriceBreakdown = {
  group: ProductInBundle[];
  grouper: {
    gameId: ProductGame;
    productType: ProductType;
    productId: string;
  };
};

type Props = {
  closeModal: () => void;
  items: BundlePriceBreakdown;
};

export const PriceBreakdownModal = ({ closeModal, items: { group, grouper } }: Props) => {
  const { t } = useI18n();

  const productsToAsk = [
    ...group.map((item) => ({
      gameId: item.gameId,
      productType: item.type as string,
      productId: item.id,
    })),
    {
      gameId: grouper.gameId,
      productType: grouper.productType,
      productId: grouper.productId,
    },
  ];

  const { data: productsInfoResponse } = useProductInfo(productsToAsk);

  const fullItems = useMemo(() => {
    const itemsToFill: {
      grouper:
        | {
            gameId: ProductGame;
            productType: ProductType;
            productId: string;
          }
        | ProductInfoData;
      group: (ProductInBundle | ProductInfoData)[];
    } = { grouper: { ...grouper }, group: [...group] };
    const fullProductsGroup: (ProductInBundle | ProductInfoData)[] = [];
    /* istanbul ignore next */
    if (!productsInfoResponse) return { grouper, group };
    productsInfoResponse.productsInfo.forEach((product) => {
      if (product.id === grouper.productId) {
        itemsToFill.grouper = { ...itemsToFill.grouper, ...product };
      } else {
        fullProductsGroup.push({ ...group.find((p) => p.id === product.id), ...product });
      }
    });
    /* istanbul ignore next: else */
    if (!!fullProductsGroup.length) {
      itemsToFill.group = fullProductsGroup;
    }
    return itemsToFill;
  }, [productsInfoResponse]);

  const CurrencyIcon = getIconForProduct(grouper.gameId as ProductGame);
  const grouperInfo = fullItems.grouper as ProductInfoData;

  return (
    <S_ListingModalContainer>
      <S_ListingBodyContainer>
        <S_CloseButton
          className="close"
          onClick={closeModal}
          buttonSize="S"
          label={(<CloseIcon />) as unknown as string}
        />
        <S_ModalTitle>{t(`dbuy.modal.price_breakdown.title.${grouper.productType.toLowerCase()}`)}</S_ModalTitle>
        <S_ProductListContainer>
          {fullItems.group.map((product, index: number) => {
            let priceModifier;
            const productInfo = product as ProductInfoData;
            const productInBundle = product as ProductInBundle;
            /* istanbul ignore next: else */
            if ((productInfo.cost || 0) < (productInfo.itemPriceInBundle || 0)) {
              priceModifier = productInfo.cost;
            }
            if (productInfo.status === PurchasabilityStatus.ALREADY_OWNED) {
              priceModifier = t("dbuy.product.buy.owned");
            }
            if (productInfo.itemPriceInBundle === 0) {
              priceModifier = t("dbuy.page.checkout.summary.free");
            }
            return (
              <S_ProductContainer key={index}>
                <div>{productInBundle.name}</div>
                <S_ProductPriceWrapper>
                  {/* Gray.04 */}
                  <CurrencyIcon size={14} fill="#0A0A0A" />
                  <span>{priceModifier}</span>
                  <S_ProductFinalPrice className={`final-price ${priceModifier ? "final-price--discounted" : ""}`}>
                    {productInfo.itemPriceInBundle || productInfo.cost}
                  </S_ProductFinalPrice>
                </S_ProductPriceWrapper>
              </S_ProductContainer>
            );
          })}
        </S_ProductListContainer>
        <S_GrouperPriceWrapper>
          {grouperInfo.cost !== grouperInfo.price && (
            <S_ProductContainer>
              <span>{t(`dbuy.modal.price_breakdown.price.original.${grouper.productType.toLowerCase()}`)}</span>
              <S_ProductPriceWrapper>
                <CurrencyIcon size={14} fill="#0A0A0A" />
                <span>{grouperInfo.price}</span>
                <span>{t(`dbuy.game_currency.${grouper.gameId}`)}</span>
              </S_ProductPriceWrapper>
            </S_ProductContainer>
          )}
          <S_ProductContainer>
            <span>{t(`dbuy.modal.price_breakdown.price.final.${grouper.productType.toLowerCase()}`)}</span>
            <S_ProductPriceWrapper>
              <CurrencyIcon size={14} fill="#0A0A0A" />
              <span>{grouperInfo.cost}</span>
              <span>{t(`dbuy.game_currency.${grouper.gameId}`)}</span>
            </S_ProductPriceWrapper>
          </S_ProductContainer>
        </S_GrouperPriceWrapper>
      </S_ListingBodyContainer>
      <S_ActionsContainer>
        {/* TODO: When we have feedback from design, this and all the productModal buttons will be updated to actually perform the purchase */}
        <S_GrouperBuy
          buttonSize="M"
          gradient
          label={
            (
              <>
                <S_ProductPriceWrapper>
                  <CurrencyIcon size={14} fill="#0A0A0A" />
                  <span>{grouperInfo.cost}</span>
                </S_ProductPriceWrapper>
                <>{t(`dbuy.modal.price_breakdown.buy.${grouper.productType.toLowerCase()}`)}</>
              </>
            ) as unknown as string
          }
        />
      </S_ActionsContainer>
    </S_ListingModalContainer>
  );
};
